import '../style.css';

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { log } from '@economic/react-app';
import { Dialog, Button, Spinner, Group } from '@economic/taco';

import { Form, Field } from '../../../../../shared/components/Form';
import { t, useLocalization } from '../../../../../shared/components/l10n/l10n';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import { BulkDiscountLocationState, BulkDiscountRequest } from '../types';

interface BulkDiscountProps {
    onSubmitted: () => void;
}

export function BulkDiscountDialog({ onSubmitted }: BulkDiscountProps) {
    const location = useLocation();
    const { draftId, selectedLineIds } = (location.state as BulkDiscountLocationState) || {};

    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>();
    const textsLoading = useLocalization(textIds);

    if (!draftId) {
        return null;
    }

    function navigateBack() {
        navigate(location.pathname);
    }

    async function submit(request: BulkDiscountRequest) {
        setLoading(true);

        try {
            await internalApiClient.post('/sales/invoicing/bulk-discount', request);
        } catch (error) {
            if (error && error.response && error.response.status >= 500) {
                log.error(error as Error);
            }
        }

        setLoading(false);
        navigateBack();
        onSubmitted();
    }

    if (loading || textsLoading) {
        return (
            <Dialog onClose={navigateBack} open={location.hash === '#bulk-discount'}>
                <Dialog.Content aria-label={t`TOAST_LOADING`}>
                    <Spinner />
                </Dialog.Content>
            </Dialog>
        );
    }

    if (!selectedLineIds || selectedLineIds.length === 0) {
        return (
            <Dialog onClose={navigateBack} open={location.hash === '#bulk-discount'}>
                <Dialog.Content aria-label={t`FORM_BULKDISCOUNT_ERROR_HEADER_CANNOTBULKDISCOUNT`}>
                    <Dialog.Title>{t`FORM_BULKDISCOUNT_ERROR_HEADER_CANNOTBULKDISCOUNT`}</Dialog.Title>

                    <p>{t`FORM_BULKDISCOUNT_ERROR_DESCRIPTION_SELECTONELINEATLEAST`}</p>

                    <Dialog.Footer>
                        <Button appearance="primary" autoFocus onClick={navigateBack}>{t`SHARED_FORM_OK`}</Button>
                    </Dialog.Footer>
                </Dialog.Content>
            </Dialog>
        );
    }

    return (
        <Dialog onClose={navigateBack} open={location.hash === '#bulk-discount'}>
            <Dialog.Content aria-label={t`FORM_BULKDISCOUNT_HEADER`} className="bulk_discount">
                <Form initialData={{ draftId, selectedLineIds }} onSubmit={submit} skipNotifications>
                    <Dialog.Title>{t`FORM_BULKDISCOUNT_HEADER`}</Dialog.Title>

                    <div className="bulk_discount__percentage_field_container">
                        <Field.Number
                            autoFocus
                            name="discountPercentage"
                            className="bulk_discount__percentage_field"
                            label={t`FORM_BULKDISCOUNT_FIELD_DISCOUNTPERCENT`}
                            decimal
                        />
                        <span className="bulk_discount__percentage_field__percentage_sign">
                            {t`FORM_BULKDISCOUNT_FIELD_DISCOUNTPERCENT_PERCENTAGE_SIGN`}
                        </span>
                    </div>

                    <Dialog.Footer>
                        <Group>
                            <Button appearance="discrete" onClick={navigateBack}>{t`FORM_BULKDISCOUNT_BUTTON_CANCEL`}</Button>
                            <Button
                                appearance="primary"
                                type="submit"
                                onClick={e => {
                                    (e.target as any).focus(); //fix for pressing enter not removing focus on input, thus not calculating value
                                }}>{t`FORM_BULKDISCOUNT_BUTTON_ADD`}</Button>
                        </Group>
                    </Dialog.Footer>
                </Form>
            </Dialog.Content>
        </Dialog>
    );
}

const textIds = [
    'TOAST_LOADING',
    'SHARED_FORM_OK',
    'FORM_BULKDISCOUNT_ERROR_HEADER_CANNOTBULKDISCOUNT',
    'FORM_BULKDISCOUNT_ERROR_DESCRIPTION_SELECTONELINEATLEAST',
    'FORM_BULKDISCOUNT_HEADER',
    'FORM_BULKDISCOUNT_FIELD_DISCOUNTPERCENT',
    'FORM_BULKDISCOUNT_FIELD_DISCOUNTPERCENT_PERCENTAGE_SIGN',
    'FORM_BULKDISCOUNT_BUTTON_CANCEL',
    'FORM_BULKDISCOUNT_BUTTON_ADD',
];
